import React, { useState } from 'react'
import { Alert, Button, IconButton, List, ListItem, ListItemText, Stack, Typography } from '@mui/material'
import { CompareArrowsOutlined, DeleteOutline, OpenInNewOutlined } from '@mui/icons-material'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { apiGet } from '../../generic/Api_Functions'
import { changeOperationVersionIndex, deleteOperationVersion, getOperationDetail, readOperationLibraryError } from './operationLibraryApi'
import OperationCompareDialog from './OperationCompareDialog'

/**
 * OperationLibraryVersions (C2, §8.7) — the version list of ONE library operation, opened from a row
 * of `OperationLibraryTable` (C1) inside a `SmartDialog`. Reads `GET operationLibrary/{code}` (N2),
 * which already returns the versions of that code; they are painted `versionNumber` DESC, so the
 * current version — the one with the HIGHEST number, whatever its index — is the first row
 * (RN-01.2, S3).
 *
 * Ajuste del PO (2026-09-15): es una `SmartMateriaUITable`, como el resto de tablas del repo — con
 * sus columnas, su acción de fila y su borrado—, no una lista a mano.
 *
 * Elegir una versión es lo que la carga en los cuatro acordeones del editor (S7): este componente
 * solo reporta la elección; la llamada `GET operationLibrary/version/{nodeId}` (N3) la hace quien lo
 * monta.
 *
 * S123 (RN-07.9, decisión del PO del 17/09/2026): comparar DOS versiones se pide DESDE AQUÍ. Se
 * eligen con las casillas de la tabla y el botón K44 abre `OperationCompareDialog` (C10, N7). Es
 * SOLO LECTURA: no cambia versiones, ni index, ni vínculos. Hasta hoy C10 estaba construido y probado
 * pero sin punto de entrada, es decir muerto en producción (hueco HS-2 del Judge).
 *
 * "Delete version" (K18) es N11 (S33): una versión vinculada por algún workflow se rechaza con 409 y
 * la lista de workflows que la retienen, y un aviso DESCARTADO sigue contando como vínculo vivo.
 */
const OperationLibraryVersions = ({
  code = '',
  selectedNodeId = '',
  canEdit = false,
  useIndex = true,
  onVersionSelected = () => {},
  onChanged = () => {},
  reloadKey = 0
}) => {
  const { t_ } = useSmartTranslation()
  const [error, setError] = useState('')
  // §8.8.6: `Version is in use by <n> workflow(s)` lleva parámetro y, por la regla heredada de #117,
  // NO se traduce: sale tal cual en inglés. Por eso el literal se guarda aparte de los traducibles.
  const [untranslatedError, setUntranslatedError] = useState('')
  const [usages, setUsages] = useState([])
  const [ownReloadKey, setOwnReloadKey] = useState(0)
  // S123: las versiones tal y como se cargaron, para poder comparar de la más antigua a la más
  // nueva sea cual sea el orden en que el usuario marque las dos casillas.
  const [loadedVersions, setLoadedVersions] = useState([])
  const [selectedNodeIds, setSelectedNodeIds] = useState([])
  const [compare, setCompare] = useState(null)

  const dataFetch = async () => {
    if (!code) return []
    try {
      const detail = await getOperationDetail(code)
      const loaded = Array.isArray(detail?.versions) ? detail.versions : []
      setError('')
      const sorted = [...loaded].sort((one, other) => (other.versionNumber || 0) - (one.versionNumber || 0))
      setLoadedVersions(sorted)
      return sorted
    } catch (thrown) {
      setError(readOperationLibraryError(thrown).error)
      return []
    }
  }

  const removeVersion = async (version) => {
    setError('')
    setUntranslatedError('')
    setUsages([])
    try {
      await deleteOperationVersion(version.nodeId)
      setOwnReloadKey((key) => key + 1)
      onChanged()
    } catch (thrown) {
      const { error: literal, context } = readOperationLibraryError(thrown)
      setUsages(Array.isArray(context.usages) ? context.usages : [])
      if (literal.includes('<n>')) {
        setUntranslatedError(literal)
      } else {
        setError(literal)
      }
    }
  }

  // S123: comparar pide exactamente DOS. Con una o con tres no hay comparación que pedir.
  const versionOf = (nodeId) => loadedVersions.find((version) => String(version.nodeId) === String(nodeId))
  const openComparison = () => {
    const chosen = selectedNodeIds
      .map(versionOf)
      .filter(Boolean)
      .sort((one, other) => (one.versionNumber || 0) - (other.versionNumber || 0))
    if (chosen.length !== 2) return
    setCompare({ fromNodeId: chosen[0].nodeId, toNodeId: chosen[1].nodeId })
  }

  const columns = [
    { name: t_('Version'), field: 'versionNumber', readOnly: true },
    // El index de una versión SÍ se edita (N10): es lo que la promueve a APPROVED, y solo una versión
    // APPROVED más alta que la vinculada dispara el aviso de actualización (RN-05.2, S85). Sin esto
    // no había forma de aprobar una versión desde la UI.
    ...(useIndex
      ? [{
          name: t_('Index Value'),
          field: 'indexValue',
          type: 'smartSelect',
          isSimpleData: true,
          mandatory: true,
          readOnly: !canEdit,
          edit: { source: async () => (await apiGet('indexValues/available') || []).map((option) => option.name) }
        }]
      : []),
    { name: t_('Created'), field: 'createdAt', type: 'datetime', readOnly: true },
    { name: t_('Created by'), field: 'createdBy', readOnly: true },
    { name: t_('Used in workflows'), field: 'workflowUsageCount', readOnly: true }
  ]

  // Elegir versión es una ACCIÓN DE FILA, no el clic en la fila: el clic en la fila lo recibe también
  // el botón de ⋮, así que abrir el menú cargaba la versión y cerraba el diálogo — no había forma de
  // llegar a "Delete version".
  const changeIndex = async (row) => {
    try {
      const indexValue = typeof row.indexValue === 'string' ? row.indexValue : (row.indexValue?.name || '')
      const version = await changeOperationVersionIndex(row.nodeId, indexValue)
      onChanged()
      return version
    } catch (thrown) {
      // La alerta de la tabla lee `message` (texto) y `name` (severidad) de lo que se lance, así que
      // el literal de Sun —que ES la clave i18n— viaja ahí traducido (E-OPL-03).
      const error = new Error(t_(readOperationLibraryError(thrown).error || 'Not allowed'))
      error.name = thrown?.name || 'warning'
      throw error
    }
  }

  const actions = {
    ...(canEdit && useIndex ? { edit: { fetchFunction: changeIndex } } : {}),
    custom: [
      {
        name: 'Select version',
        render: () => (
          <IconButton aria-label={t_('Select version')}>
            <OpenInNewOutlined />
          </IconButton>
        ),
        onClick: (row) => onVersionSelected(row)
      },
      ...(canEdit
        ? [{
            name: 'Delete version',
            render: () => (
              <IconButton aria-label={t_('Delete version')}>
                <DeleteOutline />
              </IconButton>
            ),
            onClick: removeVersion
          }]
        : [])
    ]
  }

  return (
    <Stack data-testid='operation-library-versions' gap={1} sx={{ minWidth: '40rem' }}>
      {error ? <Typography color='error' data-testid='operation-library-versions-error'>{t_(error)}</Typography> : null}
      {untranslatedError
        ? <Alert severity='warning' data-testid='operation-library-versions-usage-error'>{untranslatedError}</Alert>
        : null}
      {usages.length > 0
        ? (
          <List dense data-testid='operation-library-versions-usages'>
            {usages.map((usage) => (
              <ListItem key={String(usage.workflowId) + '-' + String(usage.nodeId)}>
                <ListItemText
                  primary={[usage.workflowCode, usage.workflowName].filter(Boolean).join(' · ')}
                  secondary={t_('Version') + ' ' + usage.workflowVersion + ' · ' + (usage.nodeCode || '')}
                />
              </ListItem>
            ))}
          </List>
          )
        : null}
      {/* S123: el botón vive sobre la tabla, y solo hace algo con dos versiones marcadas. */}
      <Stack direction='row' justifyContent='flex-end'>
        <Button
          size='small'
          variant='outlined'
          startIcon={<CompareArrowsOutlined />}
          data-testid='operation-compare-versions'
          disabled={selectedNodeIds.length !== 2}
          onClick={openComparison}
        >
          {t_('Compare versions')}
        </Button>
      </Stack>
      <SmartMateriaUITable
        columns={columns}
        actions={actions}
        dataFetch={dataFetch}
        // La ficha de una operación tiene las versiones que tiene: caben todas y no hace falta
        // buscador ni paginación (mismo trato que la tabla de usos de #117).
        primaryKeys={['nodeId']}
        searchAndPaginationVisible='none'
        tablePageSize={Number.MAX_SAFE_INTEGER}
        sticky={false}
        sortOptions={{ disableAutoSort: true }}
        forceLoad={reloadKey + ownReloadKey}
        // S123: las casillas son SOLO para elegir qué comparar — borrar en bloque no se ofrece.
        multipleSelection
        deleteMultipleSelectionDisabled
        multipleSelectionChange={(selection) => setSelectedNodeIds((selection?.rows || []).map((row) => row.nodeId))}
      />
      {/* S123: se monta solo mientras está abierto, así cada comparación parte de cero. */}
      {compare
        ? (
          <OperationCompareDialog
            open
            fromNodeId={compare.fromNodeId}
            toNodeId={compare.toNodeId}
            onClose={() => setCompare(null)}
          />
          )
        : null}
    </Stack>
  )
}

export default OperationLibraryVersions
